import {
  Sparkles,
  Cpu,
  Briefcase,
  Palette,
  GraduationCap,
  Users,
  ArrowUpRight,
} from "lucide-react";
import { tracks } from "@/data/tracks";
import { TRACKS } from "@/constants/testIds";

const iconMap = { Sparkles, Cpu, Briefcase, Palette, GraduationCap, Users };

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Bento spans: alternating layout
const spans = [
  "lg:col-span-2 lg:row-span-2",
  "lg:col-span-1",
  "lg:col-span-1",
  "lg:col-span-1",
  "lg:col-span-1",
  "lg:col-span-2",
];

export default function EventTracks() {
  return (
    <section id="tracks" data-testid={TRACKS.section} className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="eyebrow">Event Tracks</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 leading-[1.05] max-w-2xl">
              Event tracks built for different interests
            </h2>
          </div>
          <p className="font-body text-[var(--aura-charcoal-2)] max-w-md">
            A scalable brand designed to host a wellness workshop today, an AI
            meetup tomorrow, and a creator community night next month.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:auto-rows-[minmax(220px,auto)]">
          {tracks.map((t, i) => {
            const Icon = iconMap[t.icon] || Sparkles;
            const isLarge = spans[i]?.includes("row-span-2");
            return (
              <article
                key={t.id}
                data-testid={TRACKS.card(t.id)}
                className={`group relative rounded-2xl border border-black/5 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${spans[i] || ""}`}
                style={{ backgroundColor: t.theme.bg }}
              >
                <div className="relative h-full p-7 md:p-8 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{
                        backgroundColor: `${t.theme.accent}20`,
                        color: t.theme.text,
                      }}
                    >
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                    <span
                      className="font-ui text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full"
                      style={{
                        backgroundColor: t.theme.chipBg,
                        color: t.theme.chipText,
                      }}
                    >
                      {t.status}
                    </span>
                  </div>

                  <h3
                    className={`mt-6 font-display leading-tight ${isLarge ? "text-3xl md:text-4xl" : "text-2xl"}`}
                    style={{ color: t.theme.text }}
                  >
                    {t.name}
                  </h3>
                  <p
                    className="mt-3 font-body text-sm md:text-[15px] leading-relaxed"
                    style={{ color: `${t.theme.text}CC` }}
                  >
                    {t.description}
                  </p>

                  <div className="mt-auto pt-6">
                    <button
                      onClick={() => scrollTo("events")}
                      data-testid={TRACKS.cardCta(t.id)}
                      className="inline-flex items-center gap-1.5 font-ui text-sm font-medium link-underline"
                      style={{ color: t.theme.text }}
                    >
                      {t.status === "Launching first"
                        ? "View Events"
                        : "Register Interest"}
                      <ArrowUpRight
                        size={14}
                        className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </button>
                  </div>

                  {isLarge && (
                    <div
                      className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full opacity-30 blur-2xl pointer-events-none"
                      style={{ background: t.theme.accent }}
                    />
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
